import { Component, OnInit, Input, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ProjectService } from 'src/app/private/services/project.service';
import { ReportsService } from 'src/app/private/services/report.service';
import { LocalStorageService } from 'src/app/utils/LocalStorageService';
import { EpicService } from 'src/app/private/services/epic.service';
import { ProjectFeatureService } from 'src/app/private/services/project-feature.service';
import { UserStoryService } from 'src/app/private/services/user-story.service';

@Component({
  selector: 'app-project-report',
  templateUrl: './project-report.component.html',
  styleUrls: ['./project-report.component.scss'],
})
export class ProjectReportComponent implements OnInit {
  loading = true;
  taskLoading = false;
  @Input() projectId!: any;

  summary: any = {
    status: {
      open: 0,
      in_progress: 0,
      're-open': 0,
      closed: 0,
    },
  };
  grouped: any = {};
  users: any = [];
  modules: any = [];
  trend: any = [];
  currentTab: any = 0;
  reportsArray: any = ['Bug Report', 'Task Report'];
  projectData: any;

  // Task Report state
  taskSummary: any = {
    status: {},
    priority: {},
    task_type: {}
  };
  taskUsers: any = [];
  taskModules: any = [];
  taskTrend: any = {};
  taskTimeLog: any = {};
  taskLoaded = false;

  // ── Project Hierarchy State ────────────────────────────────────────────────
  listEpics: any[] = [];
  listFeatures: any[] = [];
  listUserStories: any[] = [];

  selectedListEpicId: any = null;
  selectedListFeatureId: any = null;
  selectedListUserStoryId: any = null;

  constructor(
    private localStorage: LocalStorageService,
    private projectService: ProjectService,
    private reports: ReportsService,
    private route: ActivatedRoute,
    private cdr: ChangeDetectorRef,
    private epicService: EpicService,
    private featureService: ProjectFeatureService,
    private userStoryService: UserStoryService
  ) { }

  ngOnInit() {
    this.projectId = this.route.snapshot.paramMap.get('project_id');
    this.loadReport();
    this.getProject(this.projectId);
    this.loadEpics();
  }

  loadReport() {
    this.loading = true;
    const params: any = {};
    params.client_id = this.localStorage.getClientId();

    const filters: any = {};
    if (this.selectedListEpicId) filters.epic_id = this.selectedListEpicId;
    if (this.selectedListFeatureId) filters.feature_id = this.selectedListFeatureId;
    if (this.selectedListUserStoryId) filters.user_story_id = this.selectedListUserStoryId;

    Promise.all([
      this.reports
        .getProjectSummary(this.projectId, params.client_id, filters)
        .toPromise(),
      this.reports.getBugsByUser(this.projectId, params.client_id, filters).toPromise(),
      this.reports
        .getBugsByModule(this.projectId, params.client_id, filters)
        .toPromise(),
      this.reports.getBugByTrend(this.projectId, params.client_id, filters).toPromise(),
    ])
      .then(([summary, users, modules, trend]) => {
        this.summary = summary;
        this.users = users;
        this.modules = modules;
        this.trend = trend;
      })
      .finally(() => (this.loading = false));
  }

  loadTaskReport() {
    this.taskLoading = true;
    const params: any = {};
    params.client_id = this.localStorage.getClientId();

    const filters: any = {};
    if (this.selectedListEpicId) filters.epic_id = this.selectedListEpicId;
    if (this.selectedListFeatureId) filters.feature_id = this.selectedListFeatureId;
    if (this.selectedListUserStoryId) filters.user_story_id = this.selectedListUserStoryId;

    Promise.all([
      this.reports.getTaskReportSummary(this.projectId, params.client_id, filters).toPromise(),
      this.reports.getTasksByUser(this.projectId, params.client_id, filters).toPromise(),
      this.reports.getTasksByModule(this.projectId, params.client_id, filters).toPromise(),
      this.reports.getTaskTrend(this.projectId, params.client_id, undefined, filters).toPromise(),
      this.reports.getTaskTimeLogReport(this.projectId, params.client_id, filters).toPromise(),
    ])
      .then(([summary, users, modules, trend, timelog]) => {
        this.taskSummary = summary;
        this.taskUsers = users;
        this.taskModules = modules;
        this.taskTrend = trend;
        this.taskTimeLog = timelog;
        this.taskLoaded = true;
      })
      .finally(() => (this.taskLoading = false));
  }

  fnChangeTab(event: any) {
    this.currentTab = event;
    if (this.currentTab === 1 && !this.taskLoaded && !this.taskLoading) {
      this.loadTaskReport();
    }
  }

  getTotal(key: string, data: any) {
    return data?.reduce((sum: number, item: any) => sum + (item[key] || 0), 0);
  }

  tendOwnerChange(id: any) {
    const params: any = {};
    if (id) {
      params.owner_id = id;
    }
    if (this.selectedListEpicId) params.epic_id = this.selectedListEpicId;
    if (this.selectedListFeatureId) params.feature_id = this.selectedListFeatureId;
    if (this.selectedListUserStoryId) params.user_story_id = this.selectedListUserStoryId;

    this.reports.getBugByTrend(this.projectId, this.localStorage.getClientId(), params).subscribe((res) => {
      this.trend = res;
    });
  }

  taskTrendOwnerChange(id: any) {
    const client_id = this.localStorage.getClientId();
    const params: any = {};
    if (this.selectedListEpicId) params.epic_id = this.selectedListEpicId;
    if (this.selectedListFeatureId) params.feature_id = this.selectedListFeatureId;
    if (this.selectedListUserStoryId) params.user_story_id = this.selectedListUserStoryId;

    this.reports.getTaskTrend(this.projectId, client_id, id, params).subscribe((res) => {
      this.taskTrend = res;
    });
  }

  getProject(id: any) {
    this.projectService
      .getProjectById({
        project_id: id,
        client_id: this.localStorage.getClientId(),
      })
      .subscribe((res) => {
        this.projectData = res;
      });
  }

  getGrandTotal(data: any) {
    return data?.reduce((sum: number, item: any) => sum + (item.total || 0), 0);
  }

  // ── List Hierarchy Cascading Helpers ───────────────────────────────────────
  loadEpics() {
    this.epicService.getAllEpics({
      client_id: this.localStorage.getClientId(),
      project_id: this.projectId,
      limit: 1000
    }).subscribe({
      next: (res: any) => {
        this.listEpics = res?.rows || res?.data?.rows || res?.data || [];
      }
    });
  }

  onListEpicChange(epicId: any) {
    this.selectedListEpicId = epicId ? Number(epicId) : null;
    this.selectedListFeatureId = null;
    this.selectedListUserStoryId = null;
    this.listFeatures = [];
    this.listUserStories = [];

    if (this.selectedListEpicId) {
      this.featureService.getAllFeatures({
        client_id: this.localStorage.getClientId(),
        epic_id: this.selectedListEpicId,
        limit: 1000
      }).subscribe({
        next: (res: any) => {
          this.listFeatures = res?.rows || res?.data?.rows || res?.data || [];
        }
      });
    }

    this.reloadReports();
  }

  onListFeatureChange(featureId: any) {
    this.selectedListFeatureId = featureId ? Number(featureId) : null;
    this.selectedListUserStoryId = null;
    this.listUserStories = [];

    if (this.selectedListFeatureId) {
      this.userStoryService.getAllUserStories({
        client_id: this.localStorage.getClientId(),
        feature_id: this.selectedListFeatureId,
        limit: 1000
      }).subscribe({
        next: (res: any) => {
          this.listUserStories = res?.rows || res?.data?.rows || res?.data || [];
        }
      });
    }

    this.reloadReports();
  }

  onListUserStoryChange(storyId: any) {
    this.selectedListUserStoryId = storyId ? Number(storyId) : null;
    this.reloadReports();
  }

  reloadReports() {
    this.loadReport();
    if (this.taskLoaded || this.taskLoading) {
      this.loadTaskReport();
    }
  }
}
